// import { useState, useCallback } from "react";
import { useState, useCallback, useMemo } from "react";
import {
  Box, Card, CardContent, Chip, Typography, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Menu, MenuItem, Checkbox, ListItemText,
  Button, Snackbar, Alert, Avatar, LinearProgress, alpha,
  IconButton, Tooltip, TextField, InputAdornment
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CloudIcon from "@mui/icons-material/Cloud";
import StorageIcon from "@mui/icons-material/Storage";
import MemoryIcon from "@mui/icons-material/Memory";
import SpeedIcon from "@mui/icons-material/Speed";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// ── Theme ─────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary:   { main: "#0284c7" },
    secondary: { main: "#10b981" },
    background: { default: "#f5f7fa", paper: "#ffffff" },
    text: { primary: "#1e293b", secondary: "#475569" },
  },
  typography: {
    fontFamily: '"DM Sans", "Segoe UI", sans-serif',
    h5: { fontWeight: 800, letterSpacing: "-0.5px" },
    subtitle2: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.06)",
          border: "1px solid #e2e8f0",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#f8fafc",
          color: "#475569",
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          borderBottom: "1px solid #e2e8f0",
          padding: "10px 18px",
          whiteSpace: "nowrap",
        },
        body: {
          fontSize: 13,
          color: "#0f172a",
          borderBottom: "1px solid #f1f5f9",
          padding: "14px 18px",
          verticalAlign: "middle",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover td": { backgroundColor: "#f8faff" },
          "&:last-child td": { borderBottom: "none" },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 20, height: 7, backgroundColor: "#e5e7eb" },
        bar: { borderRadius: 20 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: 10, height: 20, letterSpacing: "0.03em" },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, borderRadius: 8, fontSize: 13 },
      },
    },
  },
});

// ── Stat Card ──────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color, bg }) => (
  <Card>
    <CardContent sx={{ p: "18px 20px !important" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: 11.5 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.5, fontSize: 26, color: "text.primary" }}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{
          bgcolor: bg,
          color: color,
          width: 42,
          height: 42,
          borderRadius: "10px",
          "& svg": { fontSize: 20 },
        }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

// ── Efficiency Badge ───────────────────────────────────────────────────────────
const EfficiencyBadge = ({ efficiency, label }) => {
  const effValue = parseFloat(efficiency);
  let color = "default";

  if (!isNaN(effValue)) {
    if (effValue > 70) color = "success";
    else if (effValue > 30) color = "warning";
    else color = "error";
  }

  const valueText = !isNaN(effValue) ? `${effValue.toFixed(1)}` : "NA";
  const badgeLabel = label || (isNaN(effValue) ? "No Data" : valueText);

  return (
    <Box sx={{ 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "space-between",   // ← This creates space in the center
      width: "100%",
      gap: 2
    }}>
      {/* Value on the Left */}
      <Typography 
        variant="subtitle2" 
        sx={{ 
          fontSize: 14, 
          fontWeight: 600,
          whiteSpace: "nowrap",
          flexShrink: 0
        }}
      >
        {valueText}
      </Typography>

      {/* Badge on the Right */}
      <Chip 
        label={badgeLabel} 
        size="small" 
        color={color} 
        sx={{ height: 18, fontSize: 10, fontWeight: 700 }} 
      />
    </Box>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
export default function EKSClusterOverview({
  clusterData = null,
  onClusterSelect = null,
  selectedClusterName = null,
  onEditOpenCostUrl = null,
  onDeleteOpenCostUrl = null,
}) {
  const data = clusterData?.data ?? {};
  const clusters = data.clusterDetailsList || [];

  const isAKS = useMemo(() => {
    return !!(
      data.azureAksBillingCost ||           // Most direct indicator
      data.platform === 'aks' || 
      data.isAzure === true ||
      data.resourceGroup ||                 // AKS specific
      (clusters.length > 0 && clusters[0]?.resourceGroup) // Check first cluster
    );
  }, [data, clusters]);
  const [sortBy, setSortBy] = useState("clusterName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [colAnchor, setColAnchor] = useState(null);
  const [visibleCols, setVisibleCols] = useState({
    clusterName: true,
    region: true,
    status: true,
    totalNodes: true,
    cpuUsage: true,
    memoryUsage: true,
    costBreakdown: true,
    efficiency: true,
    idleCost: true,
    cost: true,
    potentialSavings: true
  });
  const [snack, setSnack] = useState({ open: false, msg: "" });
  const [search, setSearch] = useState("");

  const handleSort = useCallback((key) => {
    setSortOrder(sortBy === key && sortOrder === "asc" ? "desc" : "asc");
    setSortBy(key);
  }, [sortBy, sortOrder]);

  const toggleCol = useCallback((col) => {
    setVisibleCols(v => ({ ...v, [col]: !v[col] }));
  }, []);

  const filteredClusters = clusters.filter(cluster =>
    cluster.clusterName.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filteredClusters].sort((a, b) => {
    let aVal, bVal;
    switch (sortBy) {
      case "clusterName":       aVal = a.clusterName; bVal = b.clusterName; break;
      case "totalNodes":        aVal = a.totalNodes;  bVal = b.totalNodes;  break;
      case "cpuUsage":          aVal = a.clusterUsedCPU;    bVal = b.clusterUsedCPU;    break;
      case "memoryUsage":       aVal = a.clusterUsedMemory; bVal = b.clusterUsedMemory; break;
      case "efficiency":        aVal = parseFloat(a.clusterEfficiency); bVal = parseFloat(b.clusterEfficiency); break;
      case "idleCost":
        aVal = (a.clusterIdleCost === "NA" || a.clusterIdleCost == null) ? 0 : Number(a.clusterIdleCost) || 0;
        bVal = (b.clusterIdleCost === "NA" || b.clusterIdleCost == null) ? 0 : Number(b.clusterIdleCost) || 0;
        break;
      case "cost":              aVal = a.clusterTotalCost;  bVal = b.clusterTotalCost;  break;
      case "potentialSavings":  aVal = a.totalPriceValue?.savingCost || 0;  bVal = b.totalPriceValue?.savingCost || 0;  break;
      default: return 0;
    }
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortOrder === "asc" ? cmp : -cmp;
  });

  const exportCSV = useCallback(() => {
    const rows = sorted.map(c => [
      c.clusterName, c.region, c.status, c.totalNodes,
      `${c.clusterUsedCPU}/${c.totalVcpus}`,
      `${c.clusterUsedMemory}/${c.totalMemory}`,
      c.clusterEfficiency,
      typeof c.clusterTotalCost === "number" ? c.clusterTotalCost.toFixed(2) : c.clusterTotalCost,
    ].join(","));
    const csv = ["clusterName,region,status,nodes,cpu,memory,efficiency,totalCost", ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${isAKS ? "aks" : "eks"}_cluster-overview.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setSnack({ open: true, msg: "Cluster data exported!" });
  }, [sorted, isAKS]);

  // Cost pie chart data
  const nodeCostAmount = clusters.reduce((sum, c) => sum + (typeof c.clusterUsedCost === "number" ? c.clusterUsedCost : 0), 0);
  const ebsCostAmount  = data.totalClusterEBSCost || data.totalClusterDiskCost || 0;
  const lbCostAmount   = clusters.reduce((sum, c) => sum + (typeof c.clusterLoadBalancerCost === "number" ? c.clusterLoadBalancerCost : 0), 0);
  const chartData = [
    { name: "Node", value: parseFloat(nodeCostAmount.toFixed(2)), color: "#3b82f6" },
    { name: "Storage", value: parseFloat(ebsCostAmount.toFixed(2)), color: "#10b981" },
    { name: "LB", value: parseFloat(lbCostAmount.toFixed(2)), color: "#f59e0b" },
  ].filter(item => item.value > 0);

  const totalCost = data.totalClusterCost || 0;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, bgcolor: "background.default" }}>

        {/* ── Summary Stats ─────────────────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: "Total Cost",          value: `$${totalCost.toFixed(2)}`, icon: <AttachMoneyIcon />, color: "#d97706", bg: "#fef3c7" },
            { label: "Active Clusters",      value: data.totalClusters ?? 0, icon: <CloudIcon />, color: "#0284c7", bg: "#e0f2fe" },
            { label: "Total Nodes",          value: data.totalClusterNodes ?? 0, icon: <SpeedIcon />, color: "#10b981", bg: "#d1fae5" },
            { label: "CPU Utilization",      value: data.totalClusterCPU > 0 ? `${((data.totalClusterUsedCPU / data.totalClusterCPU) * 100).toFixed(1)}%` : "0%", icon: <MemoryIcon />, color: "#8b5cf6", bg: "#ede9fe" },
            { label: "Memory Utilization",   value: data.totalClusterMemory > 0 ? `${((data.totalClusterUsedMemory / data.totalClusterMemory) * 100).toFixed(1)}%` : "0%", icon: <StorageIcon />, color: "#06b6d4", bg: "#cffafe" },
            { label: "Total Idle Cost", value: `$${(data.totalIdleClusterCost || 0).toFixed(2)}`, icon: <TrendingDownIcon />, color: "#ef4444", bg: "#fee2e2" },
          ].map(card => (
            <Grid item xs={12} sm={6} md={4} key={card.label}>
              <StatCard {...card} />
            </Grid>
          ))}
        </Grid>

        {/* ── Resource Summary ──────────────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 3, alignItems: "flex-start" }}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>CPU Allocation</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Used: {data.totalClusterUsedCPU ?? 0} / {data.totalClusterCPU ?? 0} cores
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {data.totalClusterCPU > 0 ? ((data.totalClusterUsedCPU / data.totalClusterCPU) * 100).toFixed(1) : 0}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={data.totalClusterCPU > 0 ? (data.totalClusterUsedCPU / data.totalClusterCPU) * 100 : 0}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                      Free: {data.totalClusterFreeCPU ?? 0} cores
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>Memory Allocation</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Used: {(data.totalClusterUsedMemory || 0).toFixed(2)} / {data.totalClusterMemory ?? 0} GB
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {data.totalClusterMemory > 0 ? ((data.totalClusterUsedMemory / data.totalClusterMemory) * 100).toFixed(1) : 0}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={data.totalClusterMemory > 0 ? (data.totalClusterUsedMemory / data.totalClusterMemory) * 100 : 0}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                      Free: {(data.totalClusterFreeMemory || 0).toFixed(2)} GB
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>Cost Allocation</Typography>
                {chartData.length > 0 ? (
                  <Box>
                    <Box sx={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={chartData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value">
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `$${value.toFixed(2)}`} contentStyle={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px" }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box sx={{ position: "absolute", textAlign: "center" }}>
                        <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary", display: "block" }}>Total</Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "text.primary" }}>${totalCost.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, mt: -2 }}>
                      {chartData.map(item => (
                        <Box key={item.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: item.color, borderRadius: "2px" }} />
                          <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary" }}>
                            {item.name}: ${item.value.toFixed(2)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>No cost data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Cluster Details Header ──────────────────────────────────────────── */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha("#0284c7", 0.1), color: "primary.main", width: 46, height: 46, borderRadius: "12px" }}>
                  <CloudIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ lineHeight: 1.2, color: "text.primary" }}>
                    {isAKS ? "AKS" : "EKS"} Cluster Details
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                      Detailed cluster metrics & cost breakdown ·
                    </Typography>
                    <Chip
                      label={`${clusters.length} cluster${clusters.length !== 1 ? "s" : ""}`}
                      size="small"
                      sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: alpha("#0284c7", 0.1), color: "primary.main" }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* ── Controls Bar ────────────────────────────────────────────────────── */}
         <Box 
          sx={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "flex-end", 
            mb: 2.5,
            mt: 5,
            gap: 1.5,
            flexWrap: "wrap"
          }}
        >
          <TextField
            size="small"
            placeholder="Search services, clusters, DNS…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: { xs: "100%", sm: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17 }} />
                </InputAdornment>
              ),
              sx: { fontSize: 13 },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewColumnIcon sx={{ fontSize: 16 }} />}
            onClick={e => setColAnchor(e.currentTarget)}
            sx={{ height: 38, px: 2 }}
          >
            Columns
          </Button>

          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={exportCSV}
            sx={{ height: 38, px: 2 }}
          >
            Export
          </Button>
        </Box>

        {/* ── Column Toggle Menu ──────────────────────────────────────────────── */}
        <Menu
          anchorEl={colAnchor}
          open={Boolean(colAnchor)}
          onClose={() => setColAnchor(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {Object.keys(visibleCols).map(col => (
            <MenuItem key={col} onClick={() => toggleCol(col)}>
              <Checkbox checked={visibleCols[col]} size="small" />
              <ListItemText primary={col.replace(/([A-Z])/g, " $1").trim()} />
            </MenuItem>
          ))}
        </Menu>

        {/* ── Table ───────────────────────────────────────────────────────────── */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {visibleCols.clusterName && (
                    <TableCell>
                      <TableSortLabel active={sortBy === "clusterName"} direction={sortBy === "clusterName" ? sortOrder : "asc"} onClick={() => handleSort("clusterName")}>
                        Cluster Name
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {visibleCols.region && <TableCell>Region</TableCell>}
                  {visibleCols.status && <TableCell>Status</TableCell>}
                  {visibleCols.totalNodes && (
                    <TableCell>
                      <TableSortLabel active={sortBy === "totalNodes"} direction={sortBy === "totalNodes" ? sortOrder : "asc"} onClick={() => handleSort("totalNodes")}>
                        Nodes
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {visibleCols.cpuUsage && (
                    <TableCell>
                      <TableSortLabel active={sortBy === "cpuUsage"} direction={sortBy === "cpuUsage" ? sortOrder : "asc"} onClick={() => handleSort("cpuUsage")}>
                        CPU Usage
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {visibleCols.memoryUsage && (
                    <TableCell>
                      <TableSortLabel active={sortBy === "memoryUsage"} direction={sortBy === "memoryUsage" ? sortOrder : "asc"} onClick={() => handleSort("memoryUsage")}>
                        Memory Usage
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {visibleCols.costBreakdown && <TableCell>Cost Breakdown</TableCell>}
                  {visibleCols.efficiency && (
                    <TableCell>
                      <TableSortLabel active={sortBy === "efficiency"} direction={sortBy === "efficiency" ? sortOrder : "asc"} onClick={() => handleSort("efficiency")}>
                        Efficiency
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {visibleCols.idleCost && (
                    <TableCell align="right">
                      <TableSortLabel 
                        active={sortBy === "idleCost"} 
                        direction={sortBy === "idleCost" ? sortOrder : "asc"} 
                        onClick={() => handleSort("idleCost")}
                      >
                        Idle Cost
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {visibleCols.cost && (
                    <TableCell align="right">
                      <TableSortLabel active={sortBy === "cost"} direction={sortBy === "cost" ? sortOrder : "asc"} onClick={() => handleSort("cost")}>
                        Total Cost
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {visibleCols.potentialSavings && (
                    <TableCell align="right">
                      <TableSortLabel 
                        active={sortBy === "potentialSavings"} 
                        direction={sortBy === "potentialSavings" ? sortOrder : "asc"} 
                        onClick={() => handleSort("potentialSavings")}
                      >
                        Potential Savings
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell align="left" sx={{ fontWeight: 700, color: "#475569", whiteSpace: "nowrap" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      No cluster data available.
                    </TableCell>
                  </TableRow>
                ) : sorted.map(cluster => {
                  const isSelected = selectedClusterName === cluster.clusterName;

                  return (
                    <TableRow
                      key={cluster.clusterName}
                      onClick={() => onClusterSelect && onClusterSelect(cluster)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: isSelected ? alpha("#0284c7", 0.08) : "inherit",
                        borderLeft: isSelected ? "4px solid #0284c7" : "4px solid transparent",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? alpha("#0284c7", 0.14)
                            : alpha("#0284c7", 0.04),
                        },
                      }}
                    >
                      {visibleCols.clusterName && (
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: alpha("#0284c7", 0.08), color: "primary.main", width: 36, height: 36, borderRadius: "9px", "& svg": { fontSize: 17 } }}>
                              <CloudIcon />
                            </Avatar>
                            <Typography variant="subtitle2" sx={{ fontSize: 13 }}>{cluster.clusterName}</Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.region && <TableCell><Typography variant="body2" sx={{ fontSize: 13 }}>{cluster.region}</Typography></TableCell>}
                      {visibleCols.status && (
                        <TableCell>
                          <Chip label={cluster.status} size="small" color={cluster.status === "ACTIVE" || cluster.status === "Running" ? "success" : "default"} sx={{ height: 20, fontSize: 10 }} />
                        </TableCell>
                      )}
                      {visibleCols.totalNodes && <TableCell><Typography variant="body2" sx={{ fontSize: 13 }}>{cluster.totalNodes}</Typography></TableCell>}
                      {visibleCols.cpuUsage && (
                        <TableCell>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                              {cluster.clusterUsedCPU?.toFixed(2)} / {cluster.totalVcpus}
                            </Typography>
                            <LinearProgress variant="determinate" value={cluster.totalVcpus > 0 ? (cluster.clusterUsedCPU / cluster.totalVcpus) * 100 : 0} sx={{ height: 6, borderRadius: 3 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                              {cluster.totalVcpus > 0 ? ((cluster.clusterUsedCPU / cluster.totalVcpus) * 100).toFixed(1) : 0}%
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.memoryUsage && (
                        <TableCell>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                              {cluster.clusterUsedMemory?.toFixed(2)} / {cluster.totalMemory}
                            </Typography>
                            <LinearProgress variant="determinate" value={cluster.totalMemory > 0 ? (cluster.clusterUsedMemory / cluster.totalMemory) * 100 : 0} sx={{ height: 6, borderRadius: 3 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                              {cluster.totalMemory > 0 ? ((cluster.clusterUsedMemory / cluster.totalMemory) * 100).toFixed(1) : 0}%
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.costBreakdown && (
                        <TableCell>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                Node: ${typeof cluster.clusterUsedCost === "number" ? cluster.clusterUsedCost.toFixed(2) : cluster.clusterUsedCost}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                Storage: ${typeof (cluster.clusterEBSCost || cluster.clusterDiskCost) === "number" ? (cluster.clusterEBSCost || cluster.clusterDiskCost).toFixed(2) : "0"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                LB: ${typeof cluster.clusterLoadBalancerCost === "number" ? cluster.clusterLoadBalancerCost.toFixed(2) : "0"}
                              </Typography>
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontSize: 13, fontWeight: 600 }}>
                              Total: ${typeof cluster.clusterTotalCost === "number" ? cluster.clusterTotalCost.toFixed(2) : cluster.clusterTotalCost}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.efficiency && (
                        <TableCell sx={{ minWidth: 200 }}>
                          <EfficiencyBadge 
                            efficiency={cluster.clusterEfficiency} 
                            label={cluster.clusterEfficiencyLabel} 
                          />
                        </TableCell>
                      )}
                      {visibleCols.idleCost && (
                        <TableCell align="right">
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              fontSize: 13, 
                              fontWeight: 600,
                            }}
                          >
                            {cluster.clusterIdleCost === "NA" || cluster.clusterIdleCost == null 
                              ? "$0.00" 
                              : `$${(Number(cluster.clusterIdleCost) || 0).toFixed(2)}`}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleCols.cost && (
                        <TableCell align="right">
                          <Typography variant="subtitle2" sx={{ fontSize: 13, fontWeight: 600 }}>
                            ${typeof cluster.clusterTotalCost === "number" ? cluster.clusterTotalCost.toFixed(2) : cluster.clusterTotalCost}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleCols.potentialSavings && (
                        <TableCell align="right">
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              fontSize: 13, 
                              fontWeight: 600,
                              color: (cluster.totalPriceValue?.savingCost || 0) > 0 ? "success.main" : "text.secondary"
                            }}
                          >
                            ${(cluster.totalPriceValue?.savingCost || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 0.6, width: '100%' }}>
                          <Tooltip title={isSelected ? "Currently viewing this cluster" : "View cluster details"}>
                            <IconButton
                              size="small"
                              color={isSelected ? "primary" : "default"}
                              onClick={(e) => {
                                e.stopPropagation();
                                onClusterSelect(cluster);
                              }}
                              sx={{
                                color: isSelected ? "#0284c7" : "inherit",
                                bgcolor: isSelected ? alpha("#0284c7", 0.10) : "transparent",
                                "&:hover": { bgcolor: alpha("#0284c7", 0.18) },
                                borderRadius: "6px",
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {cluster.opencostUrl ? (
                            <Tooltip title="Edit OpenCost URL">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditOpenCostUrl?.(cluster);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Add OpenCost URL to enable namespace cost metrics">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditOpenCostUrl?.(cluster);
                                }}
                              >
                                <AddCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {cluster.opencostUrl && (
                            <Tooltip title="Remove OpenCost URL">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteOpenCostUrl?.(cluster);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Snackbar open={snack.open} autoHideDuration={2200} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity="success" variant="filled" sx={{ fontSize: 13, borderRadius: 2 }} onClose={() => setSnack(s => ({ ...s, open: false }))}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}